/* eslint-disable react-refresh/only-export-components */
/**
 * Breadcrumb trail for /agent-workflows/:id/runs/:executionId/metrics — shown in AppHeader
 * in place of the default brand + page title.
 */

import { useLocation, useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { ChevronRight, Loader2 } from 'lucide-react'
import { AquaLogo } from '../../assets/icons/AquaLogo'
import {
  useBranding,
  useBrandName,
  useBrandedText,
  usePlatformSuffixHidden,
} from '../../contexts/UiCustomizationContext'
import { useAppMode } from '../../contexts/AppModeContext'
import { useWorkflow } from '../../hooks/queries/useWorkflowsQuery'
import { TenantHeaderLabel } from '../branding/TenantHeaderLabel'
import { useTenantHeaderLabel } from '../../hooks/useTenantHeaderLabel'

const METRICS_PATH =
  /^\/agent-workflows\/([^/]+)\/runs\/([^/]+)\/metrics\/?$/

export function parseAgentWorkflowRunMetricsPath(pathname: string) {
  const m = pathname.match(METRICS_PATH)
  if (!m) return null
  return { workflowId: m[1], executionId: m[2] }
}

export function AppHeaderWorkflowRunMetricsCrumbs() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { isMilkyway, isDataflow } = useAppMode()
  const branding = useBranding()
  const suffixHidden = usePlatformSuffixHidden()
  const { name: brandName, isResolved: isBrandResolved } = useBrandName()
  const brandedText = useBrandedText()
  const tenantHeaderLabel = useTenantHeaderLabel()
  const parsed = parseAgentWorkflowRunMetricsPath(location.pathname)
  const workflowId = parsed?.workflowId
  const { data: workflow, isLoading } = useWorkflow(workflowId)

  if (!parsed || !workflowId) return null

  const goHome = () => {
    if (isMilkyway) navigate('/ideate')
    else if (isDataflow) {
      navigate('/ontology/migrations', { state: { openFirstGlobe: true } })
    } else navigate('/')
  }

  const crumbBtnStyle = {
    fontSize: '13px',
    color: 'var(--text-muted)',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    paddingTop: '5px',
    borderRadius: '4px',
    transition: 'all 0.15s',
  } as const

  if (isLoading) {
    return (
      <div className="app-header-workflow-crumbs" aria-busy="true">
        <Loader2 size={16} className="animate-spin" aria-hidden />
        <span className="app-header-workflow-crumbs__loading">{t('common.loading', 'Loading…')}</span>
      </div>
    )
  }

  const displayName = workflow?.name ?? workflowId

  // The company_name replaces the "AQUA" wordmark; the "Platform" suffix is
  // always appended (except in Milkyway) and rendered in the logo gradient.
  // Deployments that suppress the suffix show the brand name on its own.
  const brandText = isMilkyway || suffixHidden ? brandName : `${brandName} Platform`
  const brandMarkLabel = tenantHeaderLabel
    ? `${brandText} · ${tenantHeaderLabel}`
    : brandText

  return (
    <nav className="app-header-workflow-crumbs" aria-label="Breadcrumb">
      <button
        type="button"
        className="brand-mark"
        aria-label={isMilkyway ? brandedText(t('nav.brandMilkyway')) : brandMarkLabel}
        onClick={goHome}
        title={t('nav.goToHome')}
        // Reserve the wordmark's footprint while branding is in flight so the
        // crumbs don't shift, but never paint the fallback over a white label.
        style={isBrandResolved ? undefined : { visibility: 'hidden' }}
      >
        <AquaLogo size={17} text={brandText} gradient={branding.logo_text_gradient || undefined} />
        {tenantHeaderLabel ? <TenantHeaderLabel label={tenantHeaderLabel} /> : null}
      </button>
      <span className="breadcrumb-separator" style={{ marginLeft: '4px' }}>
        /
      </span>
      <button
        type="button"
        onClick={() => navigate('/agent-workflows')}
        style={crumbBtnStyle}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'var(--surface-alt)'
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'transparent'
        }}
        title={t('workflows.breadcrumbBackAgentWorkflows', 'Back to Agent Workflows')}
      >
        Agent Workflows
      </button>
      <ChevronRight size={12} className="app-header-workflow-crumbs__chev" aria-hidden />
      <button
        type="button"
        onClick={() => navigate(`/agent-workflows/${workflowId}/runs`)}
        style={{ ...crumbBtnStyle, marginLeft: '4px' }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'var(--surface-alt)'
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'transparent'
        }}
        title={t('workflows.breadcrumbBackRuns', 'Back to runs')}
      >
        Runs
      </button>
      <ChevronRight size={12} className="app-header-workflow-crumbs__chev" aria-hidden />
      <div className="app-header-workflow-crumbs__info">
        <span className="app-header-workflow-crumbs__name">{displayName}</span>
        {workflow?.project_name ? (
          <span className="app-header-workflow-crumbs__project">{workflow.project_name}</span>
        ) : null}
      </div>
    </nav>
  )
}
